import { useInfiniteQuery } from "@tanstack/react-query";
import { ReactComponent as OutlineDownload } from "assets/svg/outline-download.svg";
import { ReactComponent as OutlineTrash1 } from "assets/svg/outline-trash-1.svg";
import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-16.svg";
import InvoicingTag from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/components/scrollable-calendar-view/components/appointment-timeslot/components/invoicing-tag/InvoicingTag";
import Loading from "components/Loading";
import ActionsDropdown from "components/actions-dropdown/ActionsDropdown";
import ConfirmModal from "components/confirm-modal";
import ErrorTag from "components/error-tag";
import { isInvoiceWrittenOff } from "components/invoicing/utils/invoiceStatus";
import LazyImage from "components/lazy-image";
import { useUser } from "contexts/UserContext";
import useFetchInvoicesForAppointments from "hooks/useFetchInvoicesForAppointments";
import useFetchSuperbillsForAppointments from "hooks/useFetchSuperbillsForAppointments";
import messages from "messages";
import moment from "moment-timezone";
import { appointmentsV2InfiniteQueryOptions } from "queries/appointmentsV2QueryOptions";
import useFetchAllPagesForInfiniteQuery from "queries/useFetchAllPagesForInfiniteQuery";
import { useEffect, useMemo, useState } from "react";
import { Dropdown, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { Link } from "react-router-dom";
import queryBatchSuperbills from "services/queryBatchSuperbills";
import rejectSuperbill from "services/superbill/rejectSuperbill";
import { apiHostAndPath } from "utils/api";
import downloadFromDifferentOrigin from "utils/downloadFromDifferentOrigin";
import { getCoupleDisplayFullName } from "utils/getCoupleDisplayName";

import getClasses from "utils/getClasses";
import styles from "./Billing.module.scss";

const SUPERBILL_EMPTY_STATE = "/img/billing/superbill-empty-state.png";

const Billing = () => {
  const { defaultUserTimezone } = useUser();
  const [activeTab, setActiveTab] = useState("superbill");

  const appointmentsQuery = useInfiniteQuery(
    appointmentsV2InfiniteQueryOptions({}),
  );
  useFetchAllPagesForInfiniteQuery(appointmentsQuery);

  const appointments = useMemo(
    () =>
      appointmentsQuery.data?.pages.flatMap((page) => page.appointments) ?? [],
    [appointmentsQuery.data?.pages],
  );

  const isLoadingAppointments = useMemo(
    () =>
      appointmentsQuery.isLoading ||
      appointmentsQuery.isPending ||
      appointmentsQuery.hasNextPage,
    [
      appointmentsQuery.isLoading,
      appointmentsQuery.isPending,
      appointmentsQuery.hasNextPage,
    ],
  );

  const { appointmentsWithSuperBill, isLoadingSuperbills } =
    useFetchSuperbillsForAppointments(appointments || []);

  const { appointmentsWithInvoices, isLoadingInvoices } =
    useFetchInvoicesForAppointments(appointmentsWithSuperBill);

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [statementNoToDelete, setStatementNoToDelete] = useState("");

  const [error, setError] = useState<any>();
  const [dateCreatedSortDirection, setDateCreatedSortDirection] = useState<
    "asc" | "desc" | null
  >(null);

  const superbills = useMemo(() => {
    return appointmentsWithSuperBill.filter(
      (el) =>
        el.superbills?.length > 0 &&
        (el.superbills[0]?.generate_state === "up_to_date" ||
          el.superbills[0]?.generate_state === "superbill_finalized"),
    );
  }, [appointmentsWithSuperBill]);

  const [batchSuperbills, setBatchSuperbills] = useState<BatchSuperbill[]>([]);
  const [isLoadingBatchSuperbills, setIsLoadingBatchSuperbills] =
    useState(false);

  useEffect(() => {
    setIsLoadingBatchSuperbills(true);
    queryBatchSuperbills()
      .then(setBatchSuperbills)
      .catch(console.error)
      .finally(() => setIsLoadingBatchSuperbills(false));
  }, []);

  const superbillsToRender = useMemo(() => {
    const appointmentsMap = new Map(
      appointments.map((app) => [app.appointment_id, app]),
    );

    const regularSuperbills = superbills
      .filter((el) => el.superbills[0]?.orchid_pro_finalized)
      .map((el) => {
        const isCouple = el.couple?.length && el.couple.length > 1;
        const superbill = el.superbills[0];
        const pricing = el.pricing;

        return {
          clientName: isCouple
            ? getCoupleDisplayFullName(el.couple)
            : `${el.patient.first_name} ${el.patient.last_name}`,
          clientId: el.patient.cognito_sub,
          superbillNo: superbill.statement_no,
          superbillRevision: superbill.last_revision,
          dateCreated: moment
            .tz(superbill.appointment_start, defaultUserTimezone)
            .format("MM/DD/YYYY"),
          dateCreatedRaw: moment
            .tz(superbill.appointment_start, defaultUserTimezone)
            .toDate(),
          status: superbill.orchid_pro_finalized ? (
            <span className={styles["status-tag"]}>Sent via Client Portal</span>
          ) : (
            <span className={styles["status-tag--not-sent"]}>Not Sent</span>
          ),
          amountDue:
            (pricing.list_price || 0) - (pricing.amount_paid_by_patient || 0),
          amountPaid: pricing.list_price || 0,
          url: (
            <Link
              to={`/pro-dashboard/superbill/appointment/${superbill.statement_no}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`SB #${superbill.statement_no || "--"}`}
            </Link>
          ),
        };
      });

    const batchSuperbillsData = batchSuperbills
      .map((el) => {
        const appointmentsBatch = el.appointment_ids
          .map((id) => appointmentsMap.get(id))
          .filter((app): app is NonNullable<typeof app> => app !== undefined);

        if (appointmentsBatch.length === 0) return null;

        const firstAppointment = appointmentsBatch[0];
        if (!firstAppointment) return null;

        const isCouple =
          firstAppointment.couple?.length && firstAppointment.couple.length > 1;

        const totals = appointmentsBatch.reduce(
          (acc, curr) => {
            const listPrice = curr.pricing.list_price || 0;
            const amountPaid = curr.pricing.amount_paid_by_patient || 0;
            return {
              amountDue: acc.amountDue + (listPrice - amountPaid),
              amountPaid: acc.amountPaid + listPrice,
            };
          },
          { amountDue: 0, amountPaid: 0 },
        );

        return {
          clientName: isCouple
            ? getCoupleDisplayFullName(firstAppointment.couple)
            : `${firstAppointment.patient.first_name || ""} ${
                firstAppointment.patient.last_name || ""
              }`,
          clientId: firstAppointment.patient.cognito_sub,
          superbillNo: el.statement_no,
          superbillRevision: 0,
          dateCreated: moment
            .tz(el.create_datetime, defaultUserTimezone)
            .format("MM/DD/YYYY"),
          dateCreatedRaw: moment
            .tz(el.create_datetime, defaultUserTimezone)
            .toDate(),
          status: !el.sent_to_patient ? (
            <span className={styles["status-tag--not-sent"]}>Not Sent</span>
          ) : (
            <span className={styles["status-tag"]}>Sent via Client Portal</span>
          ),
          amountDue: totals.amountDue,
          amountPaid: totals.amountPaid,
          url: (
            <Link
              to={`/pro-dashboard/superbill/appointment/${el.statement_no}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`SB #${el.statement_no || "--"}`}
            </Link>
          ),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const combined = [...batchSuperbillsData, ...regularSuperbills];

    // Sort by date created if sort direction is set
    if (dateCreatedSortDirection) {
      combined.sort((a, b) => {
        const dateA = a.dateCreatedRaw?.getTime() || 0;
        const dateB = b.dateCreatedRaw?.getTime() || 0;
        return dateCreatedSortDirection === "asc"
          ? dateA - dateB
          : dateB - dateA;
      });
    }

    return combined;
  }, [
    batchSuperbills,
    superbills,
    appointments,
    defaultUserTimezone,
    dateCreatedSortDirection,
  ]);

  const filteredAppointmentsWithInvoices = useMemo(() => {
    return appointmentsWithInvoices.filter((el) => el.invoice);
  }, [appointmentsWithInvoices]);

  const isWaitingForFirstSuperbill =
    (isLoadingAppointments ||
      isLoadingSuperbills ||
      isLoadingBatchSuperbills) &&
    superbills.length === 0;

  const isWaitingForFirstInvoice =
    (isLoadingAppointments || isLoadingInvoices) &&
    filteredAppointmentsWithInvoices.length === 0;

  const onDeleteSuperbill = async (statementNo) => {
    try {
      await rejectSuperbill(statementNo);
      window.alert("delete successfully");
    } catch (error: any) {
      setError(error?.message || error.toString());
    }
    setShowConfirmDeleteModal(false);
  };

  return (
    <div className={styles["billing__container"]}>
      {error && (
        <ErrorTag
          show={true}
          onHide={() => setError(undefined)}
          duration={5000}
          errorMsg={error?.toString()}
        />
      )}
      <div className={styles["billing__top-row"]}>
        <h4 className={styles["billing__title"]}>Billing</h4>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k!)}
          id="billing-tabs"
          className={styles["billing__tabs"]}
        >
          {/* <Tab eventKey="payments" title="Payments" /> */}
          <Tab eventKey="invoices" title="Invoices" />
          <Tab eventKey="superbill" title="Superbill" />
        </Tabs>
      </div>
      <div className={styles["billing__bottom"]}>
        {activeTab === "invoices" &&
          (isWaitingForFirstInvoice ? (
            <div className={styles["billing-list-empty-state"]}>
              <Loading />
            </div>
          ) : filteredAppointmentsWithInvoices.length === 0 ? (
            <div className={styles["billing-list-empty-state"]}>
              <LazyImage
                src={SUPERBILL_EMPTY_STATE}
                width={168}
                height={"auto"}
              />
              <div className={styles["billing-list-empty-state__title"]}>
                No Invoices.
              </div>
              <div className={styles["billing-list-empty-state__sub-title"]}>
                Your invoice list will appear here.
              </div>
            </div>
          ) : (
            <Table className={styles["billing-list__table"]}>
              <thead>
                <tr>
                  <th>Date Issued</th>
                  <th>Invoice #</th>
                  <th>Status</th>
                  <th>Session Type</th>
                  <th>Client</th>
                  <th className={"text-right"}>Session Fee</th>
                  <th className={"text-right"}>Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {appointmentsWithInvoices.map(
                  ({ invoice, patient, couple }, i) => {
                    if (!invoice) return null;
                    return (
                      <tr key={`${i}-${invoice.appointment_id}`}>
                        <td>
                          {moment
                            .tz(invoice.invoice_issued_at, defaultUserTimezone)
                            .format("MM/DD/YYYY z")}
                        </td>
                        <td>
                          <Link
                            to={`/pro-dashboard/invoicing/view/appointment/${invoice.appointment_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {`#${invoice.invoice_no}`}
                          </Link>
                        </td>
                        <td>
                          <InvoicingTag invoice={invoice} />
                        </td>
                        <td>
                          {appointmentsWithInvoices[i].appointment_type_label}
                        </td>
                        <td className={styles["billing-list__table-col-name"]}>
                          {couple?.length && couple.length > 1
                            ? getCoupleDisplayFullName(couple)
                            : `${patient.first_name || ""} ${
                                patient.last_name || ""
                              }`}
                        </td>
                        <td
                          className={getClasses(
                            "text-right",
                            isInvoiceWrittenOff(invoice) && styles["no-charge"],
                          )}
                        >{`$${invoice.amount_total_invoiced}`}</td>
                        <td
                          className={getClasses(
                            "text-right",
                            isInvoiceWrittenOff(invoice) && styles["no-charge"],
                          )}
                        >{`$${invoice.amount_total_paid}`}</td>
                      </tr>
                    );
                  },
                )}
                {(isLoadingAppointments || isLoadingInvoices) && (
                  <tr key="loading-invoice">
                    <td colSpan={7} className="text-center">
                      <Spinner animation="border" size="sm" />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          ))}
        {activeTab === "superbill" &&
          (isWaitingForFirstSuperbill ? (
            <div className={styles["billing-list-empty-state"]}>
              <Loading />
            </div>
          ) : superbillsToRender.length === 0 ? (
            <div className={styles["billing-list-empty-state"]}>
              <LazyImage
                src={SUPERBILL_EMPTY_STATE}
                width={168}
                height={"auto"}
              />
              <div className={styles["billing-list-empty-state__title"]}>
                No Superbills.
              </div>
              <div className={styles["billing-list-empty-state__sub-title"]}>
                Your Superbill list will appear here.
              </div>
            </div>
          ) : (
            <Table className={styles["billing-list__table"]}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Superbill</th>
                  <th className={styles["headerWithDropdown"]}>
                    <div>Date Created</div>
                    <Dropdown>
                      <Dropdown.Toggle
                        className={styles["dropdownToggle"]}
                        variant="link"
                        bsPrefix="p-0"
                      >
                        <OutlineChevronDown16 />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className={styles["dropdownMenu"]}>
                        <Dropdown.Item
                          className={styles["dropdownItem"]}
                          onClick={() => setDateCreatedSortDirection("asc")}
                        >
                          Ascending
                        </Dropdown.Item>
                        <Dropdown.Item
                          className={styles["dropdownItem"]}
                          onClick={() => setDateCreatedSortDirection("desc")}
                        >
                          Descending
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </th>
                  <th>Status</th>
                  <th>Amount Due</th>
                  <th>Amount Paid</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {superbillsToRender.map((el, i) => {
                  return (
                    <tr key={`${i}-${el.superbillNo}`}>
                      <td className={styles["billing-list__table-col-name"]}>
                        <Link
                          to={`/pro-dashboard/patient-charts/${el.clientId}/billing`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {el.clientName}
                        </Link>
                      </td>
                      <td>
                        {/* <Link
                          to={el.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`SB #${el.superbillNo || "--"}`}
                        </Link> */}
                        {el.url}
                      </td>
                      <td>{el.dateCreated || "--"}</td>
                      <td>{el.status}</td>
                      <td>{`$${el.amountDue.toFixed(2)}`}</td>
                      <td>{`$${el.amountPaid.toFixed(2)}`}</td>
                      <td>
                        <ActionsDropdown>
                          {(() => {
                            const href = apiHostAndPath(
                              `/api/notes_secure_data/v1/superbill/appointment/pdf/${el.superbillNo}-${el.superbillRevision}`,
                            );
                            return !href ? null : (
                              <Dropdown.Item
                                onClick={() => {
                                  downloadFromDifferentOrigin(
                                    href,
                                    "superbill.pdf",
                                  );
                                }}
                              >
                                <OutlineDownload />
                                {messages.downloadSuperbill}
                              </Dropdown.Item>
                            );
                          })()}
                          <Dropdown.Item
                            className={
                              styles["dropdown-item__delete-superbill"]
                            }
                            onClick={() => {
                              setStatementNoToDelete(el.superbillNo);
                              setShowConfirmDeleteModal(true);
                            }}
                          >
                            <OutlineTrash1 />
                            {messages.deleteSuperbill}
                          </Dropdown.Item>
                        </ActionsDropdown>
                      </td>
                    </tr>
                  );
                })}
                {(isLoadingAppointments || isLoadingSuperbills) && (
                  <tr key="loading-superbill">
                    <td colSpan={7} className="text-center">
                      <Spinner animation="border" size="sm" />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          ))}
      </div>
      <ConfirmModal
        labelTitle={messages.areYouSure}
        children={messages.theCurrentSuperbillWillNotBeSaved}
        labelConfirm={messages.delete}
        show={showConfirmDeleteModal}
        onCancel={() => setShowConfirmDeleteModal(false)}
        onConfirm={() => onDeleteSuperbill(statementNoToDelete)}
      />
    </div>
  );
};

export default Billing;
